import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { CustomerDialogForm, type CustomerFormValues } from './CustomerDialogForm';
import { nameTranslationService } from '@/lib/services/nameTranslationService';
import { validateIdentity } from '@/lib/auth/identity';
import { sendInviteEmail } from '@/lib/firebase/auth/passwordReset';

const K = 'dashboard.manager.dashboard.customers.addDialog';

interface AddCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  onSuccess?: () => void;
}

type CreateManagedUserInput = {
  firstNameEn: string;
  lastNameEn: string;
  firstNameAr: string;
  lastNameAr: string;
  phone?: string;
  email?: string;
  role: 'customer';
  organizationId: string;
};
type CreateManagedUserResult = { uid: string; identity: 'phone' | 'email' | 'both' };

/** Invite toast with a retry action; never claims success for a failed send. */
export async function notifyInvite(email: string, t: (k: string, o?: any) => string) {
  const sent = await sendInviteEmail(email);
  if (sent) {
    toast.success(t(`${K}.inviteSent`, { email }));
    return;
  }
  toast.warning(t(`${K}.inviteFailed`), {
    action: { label: t(`${K}.resendInvite`), onClick: () => void notifyInvite(email, t) },
  });
}

export function AddCustomerDialog({ open, onOpenChange, organizationId, onSuccess }: AddCustomerDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  const handleSubmit = async (values: CustomerFormValues) => {
    if (!currentUser) return;
    const id = validateIdentity({ phone: values.phone, email: values.email });
    if (!id.ok) return; // the form's resolver already blocked this

    let result: CreateManagedUserResult | null = null;
    try {
      setIsLoading(true);
      const { firstNameEn, lastNameEn } = await nameTranslationService.translateToEnglish(
        values.firstNameAr,
        values.lastNameAr,
      );

      const createManagedUser = httpsCallable<CreateManagedUserInput, CreateManagedUserResult>(functions, 'createManagedUser');
      const { data } = await createManagedUser({
        firstNameEn,
        lastNameEn,
        firstNameAr: values.firstNameAr,
        lastNameAr: values.lastNameAr,
        ...(id.phone ? { phone: id.phone } : {}),
        ...(id.email ? { email: id.email } : {}),
        role: 'customer',
        organizationId,
      });
      result = data;
    } catch (error: any) {
      console.error('Error adding customer:', error);
      const msg = String(error?.message || '');
      if (msg.includes('phone-already-registered')) {
        toast.error(t(`${K}.dupPhone`));
      } else if (msg.includes('email-already-registered')) {
        toast.error(t(`${K}.dupEmail`));
      } else if (msg.includes('phone-or-email-required')) {
        toast.error(t(`${K}.phoneOrEmailRequired`));
      } else if (error?.code === 'functions/permission-denied' || msg.includes('not authorized')) {
        toast.error('You are not authorized to add a customer');
      } else if (error?.code === 'functions/unauthenticated' || msg.includes('sign-in required')) {
        toast.error('Your session expired, please sign in again');
      } else {
        toast.error('Failed to add customer');
      }
    } finally {
      setIsLoading(false);
    }

    if (!result) return;

    // Post-success work runs outside the try: a throw here (e.g. the invite
    // email failing to send) must never misreport a created account as
    // "Failed to add customer" or leave the dialog open.
    toast.success('Customer added successfully');
    if (result.identity !== 'phone' && id.email) await notifyInvite(id.email, t);
    if (result.identity !== 'email') toast.info(t(`${K}.phoneSignIn`));
    onSuccess?.();
    onOpenChange(false);
  };

  return (
    <CustomerDialogForm
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      title={t(`${K}.title`)}
    />
  );
}
